import React from 'react';
import { formatTime } from '../../utils/timeFormatter';
import { PlayIcon } from '@heroicons/react/24/outline';

/**
 * TranscriptBlockPlaceholder - Lightweight plain-text block for buffer/overscan
 *
 * Used for blocks outside the core Slate range to reduce DOM nodes and memory.
 * No Slate, no per-word spans. Click to seek, Ctrl+click to select.
 * Clicking to focus promotes block to full TranscriptBlock (handled by parent).
 */
const TranscriptBlockPlaceholder = ({
  block,
  speakers = [],
  isActive,
  isSelected,
  onSeekToBlock,
  onSeekTo,
  onPlay,
  onSelect,
  onFocus,
  isPlaying = false,
  readOnly = false,
  className = '',
}) => {
  const getBlockClasses = () => {
    const classes = [
      'group',
      'relative',
      'p-4',
      'rounded-lg',
      'border',
      'border-[1px]',
      'transition-colors',
      'duration-150',
      'hover:shadow-md',
    ];

    if (isActive) {
      classes.push('border-green-500', 'bg-green-50');
    } else if (isSelected) {
      classes.push('border-blue-500', 'bg-blue-50');
    } else {
      classes.push('border-gray-200', 'hover:border-gray-300');
    }

    return classes.join(' ');
  };

  const handleClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (e.ctrlKey || e.metaKey) {
      onSelect?.(block.id, true);
      return;
    }

    if (!readOnly) {
      onFocus?.(block.id);
    }

    if (!isPlaying) {
      try {
        await onSeekToBlock?.(block.id);
      } catch (err) {
        console.warn('[TranscriptBlockPlaceholder] Seek failed:', err);
      }
    }
  };

  const handlePlayFromStart = async (e) => {
    e.stopPropagation();
    try {
      await onSeekToBlock?.(block.id);
      onPlay?.();
    } catch (err) {
      console.warn('[TranscriptBlockPlaceholder] Play from start failed:', err);
    }
  };

  const speakerName =
    typeof block.speaker === 'string'
      ? block.speaker
      : Array.isArray(speakers) && block.speaker
        ? speakers.find((s) => s.id === block.speaker || s.name === block.speaker)?.name ?? block.speaker
        : block.speaker ?? '';

  return (
    <div
      id={`block-${block.id}`}
      tabIndex={-1}
      className={`${getBlockClasses()} ${className} ${readOnly ? 'cursor-default opacity-90' : 'cursor-pointer'}`}
      onClick={handleClick}
      style={{
        scrollMargin: 0,
        scrollPadding: 0,
        scrollSnapAlign: 'none',
      }}
    >
      {/* Header: Speaker & Timestamp */}
      <div className="flex items-center justify-between gap-3 mb-2">
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-gray-700 truncate max-w-[180px]">
            {speakerName || 'Speaker'}
          </span>
          <button
            onClick={handlePlayFromStart}
            className="p-1 hover:bg-gray-100 rounded transition-colors"
            title="Play from here"
          >
            <PlayIcon className="h-4 w-4 text-gray-600" />
          </button>
        </div>
        <span className="text-xs text-gray-500 font-mono">
          {formatTime(block.start)} → {formatTime(block.end)}
        </span>
      </div>

      {/* Plain text content - no Slate, no word spans */}
      <div
        className="text-base text-gray-900 leading-relaxed min-h-[2rem] p-3 rounded transition-all duration-200"
        style={{ letterSpacing: 'normal', wordSpacing: 'normal' }}
      >
        <span style={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}>
          {typeof block.text === 'string' ? block.text : ''}
        </span>
      </div>

      {/* Active indicator */}
      {isActive && (
        <div className="absolute left-0 top-0 bottom-0 w-1 bg-green-500 rounded-l-lg" />
      )}
    </div>
  );
};

export default React.memo(TranscriptBlockPlaceholder, (prev, next) => {
  return (
    prev.block.id === next.block.id &&
    prev.block.text === next.block.text &&
    prev.block.speaker === next.block.speaker &&
    prev.block.start === next.block.start &&
    prev.block.end === next.block.end &&
    prev.isActive === next.isActive &&
    prev.isSelected === next.isSelected &&
    prev.isPlaying === next.isPlaying &&
    prev.readOnly === next.readOnly
  );
});
